// Send windows and business days (spec 08 §5 step 5, §6). Time-zone maths come from the shared batch 2 module.
import { addBusinessDays as addBizDays } from "../gavion/time/businessDays.ts";
import {
  BUSINESS_TZ,
  localDateOf,
  zonedTimeToUtc,
} from "../gavion/time/torontoTime.ts";
import { addDays } from "../gavion/time/toronto.ts";
import { zoneParts } from "../gavion/time/zone.ts";

export const TZ = BUSINESS_TZ;
export type SendWindow = { days: readonly number[]; from: string; to: string };
export const CAMPAIGN_WINDOW: SendWindow = Object.freeze({
  days: [1, 2, 3, 4, 5],
  from: "08:00",
  to: "18:00",
});
export const DEFAULT_SEQUENCE_WINDOW: SendWindow = Object.freeze({
  days: [1, 2, 3, 4, 5],
  from: "08:30",
  to: "17:00",
});

const HM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const minutesOf = (s: string): number => {
  const m = HM.exec(s);
  if (!m) throw new Error(`bad_time:${s}`);
  return Number(m[1]) * 60 + Number(m[2]);
};

export function validateWindow(
  w: SendWindow,
): "no_days" | "bad_time" | "from_after_to" | null {
  if (
    !w.days.length ||
    !w.days.every((d) => Number.isInteger(d) && d >= 1 && d <= 7)
  )
    return "no_days";
  if (!HM.test(w.from) || !HM.test(w.to)) return "bad_time";
  return minutesOf(w.from) < minutesOf(w.to) ? null : "from_after_to";
}

const ymd = (p: { year: number; month: number; day: number }): string =>
  `${String(p.year).padStart(4, "0")}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;

/**
 * Both window ends are inclusive to the minute (18:00 sends, 18:01 does not). Deliberately differs from
 * calling hours, which use [start, end): email is not CRTC-regulated the way calls are.
 */
export function isInsideWindow(
  now: Date,
  w: SendWindow,
  holidays: readonly string[],
): boolean {
  const p = zoneParts(now, TZ);
  const mins = p.hour * 60 + p.minute;
  return (
    w.days.includes(p.weekday) &&
    !holidays.includes(ymd(p)) &&
    mins >= minutesOf(w.from) &&
    mins <= minutesOf(w.to)
  );
}

export function nextWindowStart(
  now: Date,
  w: SendWindow,
  holidays: readonly string[],
): Date {
  if (isInsideWindow(now, w, holidays)) return now;
  const p = zoneParts(now, TZ);
  const mins = p.hour * 60 + p.minute;
  const today = ymd(p);
  for (let i = 0; i <= 31; i++) {
    const d = addDays(today, i);
    const weekday = zoneParts(zonedTimeToUtc(d, "12:00", TZ), TZ).weekday;
    if (!w.days.includes(weekday) || holidays.includes(d)) continue;
    if (i === 0 && mins > minutesOf(w.to)) continue;
    return zonedTimeToUtc(d, w.from, TZ);
  }
  throw new Error("no_open_day_within_31_days");
}

/** Thin wrapper over batch 2's addBusinessDays; counting starts from the local day of `from` (America/Toronto). */
export function addBusinessDays(
  from: Date,
  n: number,
  at: string,
  businessDays: readonly number[],
  holidays: readonly string[],
): Date {
  return addBizDays(localDateOf(from, TZ), n, at, holidays, businessDays);
}

export function startOfLocalDay(now: Date): Date {
  return zonedTimeToUtc(localDateOf(now, TZ), "00:00", TZ);
}

/** GAVION campaign-fixes: the first window opening on a later Toronto day than `now` (a daily cap is spent). */
export function nextDayWindowStart(
  now: Date,
  w: SendWindow,
  holidays: readonly string[],
): Date {
  const tomorrow = zonedTimeToUtc(
    addDays(localDateOf(now, TZ), 1),
    "00:00",
    TZ,
  );
  return nextWindowStart(tomorrow, w, holidays);
}
